import { useContext, useEffect, useCallback, useRef } from 'react';
import XS from '@ww-digital/xs-sdk';

import wwUtility from '../../../ww.utility.ts';
import { MarketUtility } from '../MarketUtility.ts';
import Storage from '@ww-digital/web-palette-react/dist/components/Utility/Storage';
import GUA from '@ww-digital/xs-plugin-gua';
import type { MarketContextType } from '../../../context/market.context.ts';
import { MarketContext } from '../../../context/market.context.ts';
import { useLocation } from 'react-router-dom';

export const useExposeCustomFilters = (
  customFilters: { experimentTextId: string; customFilter: any }[],
) => {
  const { country, language } = useContext<MarketContextType>(MarketContext);
  const visitorId = Storage.getCookieValue('ww_browser_id', false);
  const { pathname } = useLocation();
  const locale = MarketUtility.getXSLocale(country, language);
  const baseURL = wwUtility.getAPIDomain();
  const hasRunExposure = useRef<Set<string>>(new Set());

  const runExposure = useCallback(
    async (experimentTextId: string) => {
      if (hasRunExposure.current.has(experimentTextId)) return; // Prevent duplicate runs

      hasRunExposure.current.add(experimentTextId);

      const gua = new GUA();
      const xs = new XS({
        user: {
          visitorId: typeof visitorId === 'string' ? visitorId : '',
          locale: locale,
        },
        baseUrl: baseURL,
        plugins: [gua],
        context: {
          locale: locale,
          app: 'ww_vs',
        },
      });

      await xs.fetchUserFeatures();
      await xs.exposeFeature(experimentTextId);
    },
    [visitorId, locale, baseURL],
  );

  const checkAndRunExposure = useCallback(
    (experimentTextId: string, customFilter: any) => {
      if (!wwUtility.isBrowser() || !customFilter?.pages.includes(pathname)) {
        return;
      }

      const sessionValue = sessionStorage.getItem(customFilter.key);
      const localValue = localStorage.getItem(customFilter.key);

      if (
        (sessionValue && sessionValue === customFilter.value) ||
        (localValue && localValue === customFilter.value)
      ) {
        runExposure(experimentTextId);
      }
    },
    [pathname, runExposure],
  );

  useEffect(() => {
    // Check all custom filters immediately
    customFilters.forEach(({ experimentTextId, customFilter }) => {
      checkAndRunExposure(experimentTextId, customFilter);
    });

    // Set up storage event listener for cross-tab communication
    const handleStorageChange = (event: StorageEvent) => {
      customFilters.forEach(({ experimentTextId, customFilter }) => {
        if (event.key === customFilter?.key) {
          checkAndRunExposure(experimentTextId, customFilter);
        }
      });
    };

    // Set up a polling mechanism for same-tab storage changes
    const pollInterval = setInterval(() => {
      customFilters.forEach(({ experimentTextId, customFilter }) => {
        checkAndRunExposure(experimentTextId, customFilter);
      });
    }, 1000); // Check every second

    // Clean up
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [customFilters, checkAndRunExposure]);

  // Reset the exposure flags when experiments change
  useEffect(() => {
    hasRunExposure.current.clear();
  }, [customFilters]);
};
